import styled from "styled-components";

export interface IColumn<T> {
    label: string;
    fieldKey: string;
    renderItem?: (item: T) => React.ReactNode;
}

export interface ITable<T> {
    items: T[];
    columns: IColumn<T>[];
}

interface IHead<T> {
    columns: IColumn<T>[];
}

interface IRow<T> {
    columns: IColumn<T>[];
    item: T;
}

const StyledTh = styled.th`
    border: 1px solid #dddddd;
    text-align: left;
    padding: 8px;
    background: #fafafa;
`;

const StyledTd = styled.td`
    border: 1px solid #dddddd;
    text-align: left;
    padding: 8px;
`;

const StyledTable = styled.table`
    font-family: arial, sans-serif;
    border-collapse: collapse;
    width: 100%;
    background: white;
`;

const GenericTable = <T extends any>() => {
    const Head: React.FC<IHead<T>> = ({ columns }) => {
        return (
            <thead>
                <tr>
                    {columns.map((v, i) => (
                        <StyledTh key={i}>{v.label}</StyledTh>
                    ))}
                </tr>
            </thead>
        );
    };

    const Row: React.FC<IRow<T>> = ({ columns, item }) => {
        const getField = (item: any, fieldKey: string) => {
            let result = item;
            fieldKey.split(".").forEach((v) => {
                if (result) {
                    result = result[v];
                }
            });
            if (!result) result = "-";
            return result;
        };

        return (
            <tr>
                {columns.map((col, i) => {
                    if (col.renderItem)
                        return (
                            <StyledTd key={i}>
                                {col.renderItem(item) ?? "-"}
                            </StyledTd>
                        );

                    return (
                        <StyledTd key={i}>
                            {getField(item, col.fieldKey)}
                        </StyledTd>
                    );
                })}
            </tr>
        );
    };

    const Rows: React.FC<ITable<T>> = ({ columns, items }) => {
        return (
            <tbody>
                {items?.map((v, i) => (
                    <Row key={i} columns={columns} item={v} />
                ))}
            </tbody>
        );
    };

    const CompleteTable: React.FC<ITable<T>> = ({ items, columns }) => {
        return (
            <StyledTable>
                <Head columns={columns} />
                <Rows columns={columns} items={items} />
            </StyledTable>
        );
    };

    return CompleteTable;
};

export default GenericTable;
